import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Typography,
  Space,
  message,
  Tabs,
  InputNumber,
  Alert,
} from 'antd';
import {
  SettingOutlined,
  KeyOutlined,
  ApiOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [generalForm] = Form.useForm();
  const [apiForm] = Form.useForm();
  const [chatForm] = Form.useForm();

  // 加载所有设置
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // 加载所有设置
        const response = await api.get('/settings');
        if (response.data.success && response.data.data) {
          const settings = response.data.data;
          // 设置基础设置表单
          generalForm.setFieldsValue({
            siteName: settings.siteName || 'Gary Strategy Club',
            siteDescription: settings.siteDescription || '专业的交易策略分享平台',
            contactEmail: settings.contactEmail || 'support@garyclub.com',
            maintenanceMode: settings.maintenanceMode === 'true',
            registrationEnabled: settings.registrationEnabled !== 'false',
            leaderboardCtaLink: settings.leaderboardCtaLink || '/register',
          });
          // 设置客服链接
          chatForm.setFieldsValue({ customerServiceUrl: settings.customerServiceUrl || '' });
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, [generalForm, chatForm]);

  const handleGeneralSave = async (values) => {
    setLoading(true);
    try {
      // 保存所有基础设置
      await api.post('/settings/batch', {
        settings: {
          siteName: values.siteName,
          siteDescription: values.siteDescription,
          contactEmail: values.contactEmail,
          maintenanceMode: values.maintenanceMode ? 'true' : 'false',
          registrationEnabled: values.registrationEnabled ? 'true' : 'false',
          leaderboardCtaLink: values.leaderboardCtaLink || '/register',
        }
      });
      message.success('基础设置已保存');
    } catch (error) {
      message.error('保存失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApiSave = async (values) => {
    setLoading(true);
    setTimeout(() => {
      message.success('API设置已保存');
      setLoading(false);
    }, 1000);
  };

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined />
          基础设置
        </span>
      ),
      children: (
        <Form
          form={generalForm}
          layout="vertical"
          onFinish={handleGeneralSave}
          initialValues={{
            siteName: 'Gary Strategy Club',
            siteDescription: '专业的交易策略分享平台',
            contactEmail: 'support@garyclub.com',
            maintenanceMode: false,
            registrationEnabled: true,
            leaderboardCtaLink: '/register',
          }}
        >
          <Form.Item name="siteName" label="网站名称">
            <Input />
          </Form.Item>
          <Form.Item name="siteDescription" label="网站描述">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="contactEmail" label="联系邮箱">
            <Input />
          </Form.Item>
          <Form.Item
            name="leaderboardCtaLink"
            label="排行榜 Get Started 跳转链接"
            extra="支持站内路径（如 /register、/vip）或完整外链（https://...）"
          >
            <Input placeholder="/register" />
          </Form.Item>
          <Form.Item name="maintenanceMode" label="维护模式" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="registrationEnabled" label="开放注册" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              保存设置
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'api',
      label: (
        <span>
          <ApiOutlined />
          API配置
        </span>
      ),
      children: (
        <Form
          form={apiForm}
          layout="vertical"
          onFinish={handleApiSave}
          initialValues={{
            deepseekApiKey: '',
            deepseekApiUrl: 'https://api.deepseek.com/v1/chat/completions',
            maxTokens: 2000,
            temperature: 0.7,
          }}
        >
          <Alert
            message="DeepSeek AI 配置"
            description="配置 DeepSeek API 以启用 AI 选股和诊断功能"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Form.Item
            name="deepseekApiKey"
            label="DeepSeek API Key"
            extra="请妥善保管您的API密钥"
          >
            <Input.Password prefix={<KeyOutlined />} placeholder="sk-..." />
          </Form.Item>
          <Form.Item name="deepseekApiUrl" label="API URL">
            <Input />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="maxTokens" label="最大Token数">
              <InputNumber min={100} max={4000} />
            </Form.Item>
            <Form.Item name="temperature" label="Temperature">
              <InputNumber min={0} max={2} step={0.1} />
            </Form.Item>
          </Space>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              保存API设置
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'chat',
      label: (
        <span>
          <MessageOutlined />
          在线客服配置
        </span>
      ),
      children: (
        <div>
          <Alert
            message="在线客服链接配置"
            description="配置客服链接，用户点击「立即完成支付」时将打开此链接进行咨询"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form
            form={chatForm}
            layout="vertical"
            onFinish={async (values) => {
              setLoading(true);
              try {
                await api.put('/settings/customerServiceUrl', { 
                  value: values.customerServiceUrl || '',
                  description: '在线客服链接'
                });
                message.success('客服链接已保存');
              } catch (error) {
                message.error('保存失败：' + (error.response?.data?.message || error.message));
              } finally {
                setLoading(false);
              }
            }}
          >
            <Form.Item
              name="customerServiceUrl"
              label="客服链接"
              extra="用户点击「立即完成支付」按钮时将跳转到此链接"
              rules={[{ type: 'url', message: '请输入有效的URL链接' }]}
            >
              <Input 
                placeholder="例如：https://wa.me/1234567890 或 https://t.me/your_username" 
                size="large"
              />
            </Form.Item>

            <Card size="small" style={{ marginBottom: 24, background: 'rgba(251, 191, 36, 0.05)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
              <Text strong style={{ color: '#faad14' }}>常用客服链接格式：</Text>
              <div style={{ marginTop: 12 }}>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text code>WhatsApp:</Text> https://wa.me/国家代码+手机号
                </Paragraph>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text code>Telegram:</Text> https://t.me/用户名
                </Paragraph>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text code>微信客服:</Text> 您的企业微信客服链接
                </Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text code>SaleSmartly:</Text> 您的 SaleSmartly 聊天链接
                </Paragraph>
              </div>
            </Card>

            <Alert
              message="使用说明"
              description="保存后，前端VIP页面的「立即完成支付」按钮会跳转到您配置的客服链接，方便用户联系客服完成支付。"
              type="success"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} size="large">
                保存客服链接
              </Button>
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Title level={4}>系统设置</Title>

      <Card>
        <Tabs items={tabItems} />
      </Card>
    </Space>
  );
};

export default Settings;
